import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IDResource } from 'types';
import { clamp } from 'utils/helpers/resourcesMap';
import styles from './index.module.scss';

interface TooltipProps {
  elementBox: DOMRect | undefined;
  hoveredElement?: IDResource | SVGElement;
  bgColor?: string;
}

const defaultPosition = { x: 0, y: 0 };
const defaultSize = { width: 0, height: 0 };
const defaultBgColor = 'rgba(255, 255, 255, 0.8)';

const Tooltip: React.FC<TooltipProps> = ({
  hoveredElement,
  elementBox,
  bgColor,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipSize, setTooltipSize] = useState(defaultSize);

  useEffect(
    () =>
      setTooltipSize(
        tooltipRef.current?.getBoundingClientRect() || defaultSize
      ),
    [elementBox, tooltipRef]
  );

  const triangleSize = useMemo(() => {
    const size = elementBox?.height || 0;
    return Math.ceil(size / 8);
  }, [elementBox]);

  const ElPosition = useMemo(() => {
    if (!hoveredElement || !elementBox) {
      return defaultPosition;
    }

    const offset = elementBox.width / 3;

    const posX = elementBox.x + elementBox.width / 2 - tooltipSize.width / 2;
    const posY = elementBox.y - tooltipSize.height - offset;

    const clampedX = clamp(posX, 0, window.innerWidth - tooltipSize.width);
    const clampedY = clamp(posY, 0, window.innerHeight - tooltipSize.height);

    return {
      x: clampedX,
      y: clampedY,
    };
  }, [hoveredElement, elementBox, tooltipSize]);

  const opacity = useMemo(
    () => (Object.values(tooltipSize).some((v) => v === 0) ? 0 : 1),
    [tooltipSize]
  );

  const triangleStyles = useMemo(() => {
    if (!elementBox) {
      return {};
    }

    const posX = elementBox.x + elementBox.width / 2 - tooltipSize.width / 2;
    const leftPos = (() => {
      if (posX < 0) {
        return tooltipSize.width / 2 - triangleSize - Math.abs(posX);
      }
      if (posX + tooltipSize.width > window.innerWidth) {
        return (
          tooltipSize.width / 2 -
          triangleSize -
          (window.innerWidth - Math.abs(posX) - tooltipSize.width)
        );
      }
      return tooltipSize.width / 2 - triangleSize;
    })();

    return {
      top: tooltipSize.height,
      left: leftPos,
      borderWidth: `${
        triangleSize + 1
      }px ${triangleSize}px 0 ${triangleSize}px`,
      borderColor: `${
        bgColor || defaultBgColor
      } transparent transparent transparent`,
      transform: 'rotate(360deg)',
    };
  }, [elementBox, bgColor, tooltipSize, triangleSize]);

  if (!hoveredElement || !elementBox) {
    return null;
  }

  const { link } = hoveredElement as IDResource;

  return (
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      style={{
        transform: `translate(${ElPosition.x}px, ${ElPosition.y}px)`,
        opacity,
        backgroundColor: bgColor || defaultBgColor,
      }}
    >
      <div className={styles.triangle} style={triangleStyles} />
      <span className={styles.tooltip__title}>{link}</span>
    </div>
  );
};

export default Tooltip;
